/**
 * w-profile-carousel.js
 * ─────────────────────────────────────────────────────────────
 * Profile Carousel — Vanilla JS, no dependencies.
 * Exportable as an ES6 module. Safe on pages 
 * that do not contain the carousel element (does not throw errors).
 *
 * Use as a module:
 *   import initProfileCarousel from './w-profile-carousel.js';
 *   initProfileCarousel();
 *
 * Global usage (without bundler):
 *   <script type="module" src="w-profile-carousel.js"></script>
 *   It auto-initializes when the DOM is loaded.
 */

const BREAKPOINTS = [
  { minWidth: 1500, cols: 6, rows: 2 },
  { minWidth: 1160, cols: 5, rows: 2 },
  { minWidth: 900,  cols: 4, rows: 2 },
  { minWidth: 700,  cols: 3, rows: 2 },
  { minWidth: 480,  cols: 2, rows: 2 },
  { minWidth: 0,    cols: 1, rows: 1 }, // mobile: 1 column, 1 row
];

const getBreakpoint = () => {
  const w = window.innerWidth;
  for (const bp of BREAKPOINTS) {
    if (w >= bp.minWidth) return bp;
  }
  return BREAKPOINTS[BREAKPOINTS.length - 1];
};

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class ProfileCarousel {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('.wpc-carousel__viewport');
    this.btnPrev = root.querySelector('.wpc-carousel__btn--prev');
    this.btnNext = root.querySelector('.wpc-carousel__btn--next');
    this.pagination = document.getElementById(`${root.id}-pagination`);

    /* We saved the original <li class="wpc-slide"> from the CFM */
    this._sourceTrack = root.querySelector('.wpc-carousel__track');
    if (!this._sourceTrack) return;
    this._sourceSlides = Array.from(this._sourceTrack.querySelectorAll('.wpc-slide'));

    if (this._sourceSlides.length === 0) return;

    this.currentIndex = 0;
    this.isTransitioning = false;

    /* Create announcer before any call to _goToReal */
    this._announcer = this._createAnnouncer();

    this._build(); // Build groups + real track + pagination
    this._bindEvents();
  }

  /* 
    ───────────── Build (or rebuild) the entire track ───────────────────
    It is called in the constructor and in every resize that changes bp.
    ───────────────────────────────────────────────────────────────────── 
  */
  _build() {
    const bp = getBreakpoint();
    this.cols = bp.cols;
    this.rows = bp.rows;

    /* How many cards fit on a slide */
    this.cardsPerSlide = this.cols * this.rows;

    /* Inject CSS variables to make the layout work */
    this.root.style.setProperty('--wpc-cols', this.cols);
    this.root.style.setProperty('--wpc-rows', this.rows);

    /* Group original cards on pages */
    this._pages = this._chunkSlides(this._sourceSlides, this.cardsPerSlide);
    this.totalPages = this._pages.length;

    /* 
      Replace the existing track with a new one.
      It also removes the original track from the CFM if it is still in the DOM. 
    */
    if (this._track) this._track.remove();

    if ( this._sourceTrack && this._sourceTrack.parentNode === this.viewport ) {
      this._sourceTrack.remove();
    }

    this._track = this._buildTrack();
    this.viewport.appendChild(this._track);

    /* Rebuild paging */
    this._buildPagination();

    /* Go to the beginning without animation */
    const safeIndex = Math.min(this.currentIndex, this.totalPages - 1);
    this._goToPage(safeIndex, false);
  }

  /* ── Split array into chunks of size N ── */
  _chunkSlides(slides, size) {
    const pages = [];
    for (let i = 0; i < slides.length; i += size) {
      pages.push(slides.slice(i, i + size));
    }
    return pages;
  }

  /* ── Build the <ul> of the track with clones for loop ── */
  _buildTrack() {
    const track = document.createElement('ul');
    track.className = 'wpc-carousel__track';
    track.setAttribute('role', 'list');
    track.setAttribute('aria-live', 'polite');

    /* Clone last page to the beginning (for loop prev) */
    const lastPageClone = this._buildPageEl(this._pages[this._pages.length - 1], true);
    track.appendChild(lastPageClone);

    /* Real pages */
    this._pageEls = [];
    this._pages.forEach((pageCards, idx) => {
      const el = this._buildPageEl(pageCards, false, idx);
      track.appendChild(el);
      this._pageEls.push(el);
    });

    /* Clone first page to end (for loop next) */
    const firstPageClone = this._buildPageEl(this._pages[0], true);
    track.appendChild(firstPageClone);

    /* offset = 1 (the initial clone) */
    this._trackOffset = 1;

    return track;
  }

  /* ── Build a page element (slide-group) ── */
  _buildPageEl(cards, isClone, pageIdx = null) {
    const li = document.createElement('li');
    li.className = 'wpc-page';
    if (isClone) li.setAttribute('aria-hidden', 'true');

    if (!isClone && pageIdx !== null) {
      li.setAttribute('role', 'group');
      li.setAttribute('aria-roledescription', 'slide');
      li.setAttribute('aria-label', `Page ${pageIdx + 1} of ${this._pages.length}`);
    }

    const grid = document.createElement('ul');
    grid.className = 'wpc-page__grid';
    grid.setAttribute('role', 'list');

    cards.forEach(originalLi => {
      /* Clone the original <li class="wpc-slide"> from the CFM */
      const clone = originalLi.cloneNode(true);
      if (isClone) clone.setAttribute('aria-hidden', 'true');
      grid.appendChild(clone);
    });

    li.appendChild(grid);
    return li;
  }

  /* ── Announcer for screen readers ── */
  _createAnnouncer() {
    const el = document.createElement('div');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.className = 'wpc-sr-only';
    this.root.appendChild(el);
    return el;
  }

  _announce(text) {
    this._announcer.textContent = '';
    setTimeout(() => { this._announcer.textContent = text; }, 50);
  }

  /* ── Pagination ── */
  _buildPagination() {
    if (!this.pagination) return;
    this.pagination.innerHTML = '';
    this.dots = [];

    for (let i = 0; i < this.totalPages; i++) {
      const btn = document.createElement('button');
      btn.className = 'wpc-pagination__dot';
      btn.setAttribute('aria-label', `Go to page ${i + 1}`);
      btn.addEventListener('click', () => this.goTo(i));
      this.pagination.appendChild(btn);
      this.dots.push(btn);
    }
    this._updateDots();
  }

  _updateDots() {
    if (!this.dots) return;
    this.dots.forEach((d, i) => {
      const active = i === this.currentIndex;
      d.classList.toggle('wpc-pagination__dot--active', active);
      d.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  /* ── Track movement ── */
  _moveTo(pageIdx, animate = true) {
    /* Each page occupies 100% of the viewport */
    const x = -((pageIdx + this._trackOffset) * 100);
    const useAnim = animate && !prefersReducedMotion();
    this._track.style.transition = useAnim
      ? 'transform 0.38s cubic-bezier(.4,0,.2,1)'
      : 'none';
    this._track.style.transform = `translateX(${x}%)`;
  }

  _goToPage(index, animate = true) {
    this.currentIndex = ((index % this.totalPages) + this.totalPages) % this.totalPages;
    this._moveTo(this.currentIndex, animate);
    this._updateDots();
    this._announce(`Page ${this.currentIndex + 1} of ${this.totalPages}`);
  }

  goTo(index) {
    if (this.isTransitioning) return;
    this._goToPage(index);
  }

  next() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    const nextIdx = this.currentIndex + 1;
    this._moveTo(nextIdx); // can go to the clone

    if (nextIdx >= this.totalPages) {
      this._onTransitionEnd(() => {
        this._goToPage(0, false);
        this.isTransitioning = false;
      });
    } else {
      this.currentIndex = nextIdx;
      this._updateDots();
      this._announce(`Page ${this.currentIndex + 1} of ${this.totalPages}`);
      this._onTransitionEnd(() => { this.isTransitioning = false; });
    }
  }

  prev() {
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    const prevIdx = this.currentIndex - 1;
    this._moveTo(prevIdx); // can go to the clone

    if (prevIdx < 0) {
      this._onTransitionEnd(() => {
        this._goToPage(this.totalPages - 1, false);
        this.isTransitioning = false;
      });
    } else {
      this.currentIndex = prevIdx;
      this._updateDots();
      this._announce(`Page ${this.currentIndex + 1} of ${this.totalPages}`);
      this._onTransitionEnd(() => { this.isTransitioning = false; });
    }
  }

  _onTransitionEnd(cb) {
    if (prefersReducedMotion()) { cb(); return; }

    let hasRun = false;
    let timeoutId;

    const done = () => {
      if (hasRun) return;
      hasRun = true;
      clearTimeout(timeoutId);
      cb();
    };

    this._track.addEventListener('transitionend', done, { once: true });
    timeoutId = setTimeout(done, 420); // fallback
  }

  /* ── Resize ── */
  _rebuild() {
    const bp = getBreakpoint();
    /* Only rebuild if cols or rows change */
    if (bp.cols === this.cols && bp.rows === this.rows) return;
    this._build();
  }

  /* ── Events ── */
  _bindEvents() {
    this.btnPrev?.addEventListener('click', () => this.prev());
    this.btnNext?.addEventListener('click', () => this.next());

    this.root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
    });

    let startX = null;
    this.viewport.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.viewport.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) diff > 0 ? this.next() : this.prev();
      startX = null;
    }, { passive: true });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this._rebuild(), 200);
    });
  }
}

/* 
  ── Init ────────────────────────────────────────────────────────
    Safe on pages without carousels: exits silently.
  ─────────────────────────────────────────────────────────────── 
*/
const initProfileCarousel = () => {
  const initializeCarousels = () => {
    const carousels = document.querySelectorAll('.wpc-carousel');
    if (carousels.length === 0) return;
    carousels.forEach(el => new ProfileCarousel(el));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCarousels, { once: true });
    return;
  }

  initializeCarousels();
};

export { initProfileCarousel };
export default initProfileCarousel;